// deriv.js — shared helpers for pulling live market data from Deriv's
// public, unauthenticated WebSocket endpoint (no app_id / login required).

export const DERIV_PUBLIC_WS_URL = "wss://api.derivws.com/trading/v1/options/ws/public";

// Fallback list, used only until the live active_symbols fetch resolves
// (or if it fails). Kept in sync with what Deriv actually returns for
// underlying_symbol so the UI never shows a dead symbol like "1HZ90V".
export const VOLATILITY_SYMBOLS = [
  { name: "Volatility 10 Index", symbol: "R_10" },
  { name: "Volatility 25 Index", symbol: "R_25" },
  { name: "Volatility 50 Index", symbol: "R_50" },
  { name: "Volatility 75 Index", symbol: "R_75" },
  { name: "Volatility 100 Index", symbol: "R_100" },
  { name: "Volatility 10 (1s) Index", symbol: "1HZ10V" },
  { name: "Volatility 15 (1s) Index", symbol: "1HZ15V" },
  { name: "Volatility 25 (1s) Index", symbol: "1HZ25V" },
  { name: "Volatility 30 (1s) Index", symbol: "1HZ30V" },
  { name: "Volatility 50 (1s) Index", symbol: "1HZ50V" },
  { name: "Volatility 75 (1s) Index", symbol: "1HZ75V" },
  { name: "Volatility 100 (1s) Index", symbol: "1HZ100V" },
];

const VOLATILITY_SYMBOL_SET = new Set(VOLATILITY_SYMBOLS.map((v) => v.symbol));

// Fetches the live tradable-symbol list from Deriv's public endpoint and
// resolves with only the open, non-suspended volatility indices this app
// uses. Falls back to VOLATILITY_SYMBOLS on any error or timeout.
export const fetchVolatilitySymbols = () =>
  new Promise((resolve) => {
    let settled = false;
    const ws = new WebSocket(DERIV_PUBLIC_WS_URL);

    const finish = (symbols) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      try {
        ws.close();
      } catch {}
      resolve(symbols);
    };

    const timeout = setTimeout(() => finish(VOLATILITY_SYMBOLS), 8000);

    ws.onopen = () => ws.send(JSON.stringify({ active_symbols: "brief", req_id: 1 }));

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.error) {
          finish(VOLATILITY_SYMBOLS);
          return;
        }
        if (!Array.isArray(data.active_symbols)) return;

        const live = data.active_symbols
          .filter(
            (entry) =>
              VOLATILITY_SYMBOL_SET.has(entry.underlying_symbol) &&
              entry.exchange_is_open &&
              !entry.is_trading_suspended
          )
          .map((entry) => ({
            name: entry.underlying_symbol_name,
            symbol: entry.underlying_symbol,
          }));

        finish(live.length ? live : VOLATILITY_SYMBOLS);
      } catch {
        finish(VOLATILITY_SYMBOLS);
      }
    };

    ws.onerror = () => finish(VOLATILITY_SYMBOLS);
  });

// Opens a public (unauthenticated) tick subscription for a single symbol
// against the same public endpoint. Returns the WebSocket so callers can
// close it on cleanup.
export const subscribeToTicks = (symbol, onQuote) => {
  const ws = new WebSocket(DERIV_PUBLIC_WS_URL);

  ws.onopen = () => ws.send(JSON.stringify({ ticks: symbol, subscribe: 1 }));

  ws.onmessage = (event) => {
    const data = JSON.parse(event.data);
    if (data.tick?.quote !== undefined) onQuote(data.tick.quote);
  };

  ws.onerror = (err) => console.error(`[deriv] tick stream error for ${symbol}:`, err);

  return ws;
};
